/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import type ReviewSchemaPart from 'pages/open-course/peerReview/reviewTypes/structured/partTypes/multiLineInput/models/schema';

import { Typography2 } from '@coursera/cds-core';
import { InfoOutlineIcon } from '@coursera/cds-icons';

import InlineValidationErrorV2 from 'bundles/assess-common/components/InlineValidationErrorV2';
import type { MultiLineStructuredPart, MultiLineStructuredPartDefinition } from 'bundles/assess-common/types/Reviews';
import type { ValidationError } from 'bundles/assess-common/types/ValidationErrors';
import NumericInput from 'bundles/author-common/components/NumericInput';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { ReduceLowValueFeedbackContext } from 'bundles/peer/components/ReduceLowValueFeedbackProvider';
import Content from 'bundles/phoenix/components/Content';

import _t from 'i18n!nls/assess-common';

import 'css!./__styles__/MultiLineInputFormPart';

const styles = {
  peerReviewViewFeedbackTips: () => css`
    border: 1px solid var(--cds-color-neutral-stroke-primary);
    border-top: none;
    background-color: var(--cds-color-neutral-background-primary-weak);
    padding: var(--cds-spacing-50);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: var(--cds-spacing-100);
    width: 100%;
  `,

  peerReviewMinCharCount: () => css`
    margin-top: var(--cds-spacing-100);
  `,
};

type Props = {
  disabled: boolean;
  reviewSchemaPart: typeof ReviewSchemaPart;
  reviewPart?: MultiLineStructuredPart;
  onChange?: (change: MultiLineStructuredPartDefinition) => void;
  validationError?: ValidationError | null;
};

type State = {
  score: number | undefined | null;
  textareaValue: string;
};

class MultiLineInputFormPart extends React.Component<Props, State> {
  state: State = {
    score: undefined,
    textareaValue: '',
  };

  constructor(props: Props) {
    super(props);

    if (props.reviewPart) {
      this.state = {
        score: props.reviewPart.definition.score,
        textareaValue: props.reviewPart.definition.input || '',
      };
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.reviewPart && nextProps.reviewPart !== this.props.reviewPart) {
      this.setState({
        score: nextProps.reviewPart.definition.score,
        textareaValue: nextProps.reviewPart.definition.input || '',
      });
    }
  }

  handleChange = (event: React.ChangeEvent<HTMLElement>) => {
    if (!this.props.disabled && this.props.onChange && event.target instanceof HTMLTextAreaElement) {
      const textareaValue = event.target.value || '';
      this.props.onChange({ input: textareaValue });
      this.setState({ textareaValue });
    }
  };

  handleChangeScore = (score: number) => {
    if (!this.props.disabled && this.props.onChange) {
      // Scores under 0 are disallowed by the `NumericInput` component
      const windowedScore = Math.min(score, this.props.reviewSchemaPart.getMaxScore());
      if (this.props.onChange) {
        this.props.onChange({ score: windowedScore });
      }
      this.setState({ score: windowedScore });
    }
  };

  render() {
    const { disabled, reviewSchemaPart, validationError } = this.props;
    const promptId = reviewSchemaPart.id + '-prompt';
    const validationLabelId = reviewSchemaPart.id ? `form-part-label-${reviewSchemaPart.id}` : undefined;

    return (
      <div className="rc-MultiLineInputFormPart">
        <div className="multiline-prompt">
          <Content contentId={promptId} content={reviewSchemaPart.get('prompt')} />
        </div>
        {reviewSchemaPart.isScored() && (
          <div className="horizontal-box align-items-vertical-center" style={{ marginTop: 12, marginBottom: 12 }}>
            <div className="color-secondary-text label-text" style={{ marginRight: 12 }}>
              <FormattedMessage message={_t('Score (out of {points})')} points={reviewSchemaPart.getMaxScore()} />
            </div>
            <NumericInput
              disabled={disabled}
              initialValue={this.state.score}
              min={0}
              max={reviewSchemaPart.getMaxScore()}
              allowDecimal={false}
              allowNegative={false}
              onChange={this.handleChangeScore}
              ariaLabel={_t('Enter score here')}
              ariaDescribedBy={promptId}
            />
          </div>
        )}
        <InlineValidationErrorV2 id={`${promptId}~${validationLabelId}`} validationError={validationError} />
        <textarea
          className="c-peer-review-submit-textarea-input-field"
          disabled={disabled}
          onChange={this.handleChange}
          value={this.state.textareaValue}
          aria-label={_t('Enter text here')}
          aria-describedby={`${promptId}~${validationLabelId}`}
        />
        <ReduceLowValueFeedbackContext.Consumer>
          {(contextValue) => {
            const { feedbackTipsEnabled, openFeedbackTips, minCharacterCountEnabled, minCount } = contextValue;
            const charCount = this.state.textareaValue.replace(/\s+/g, '').length;

            return (
              <React.Fragment>
                {feedbackTipsEnabled && (
                  <TrackedButton
                    trackingName="view_feedback_tips"
                    css={styles.peerReviewViewFeedbackTips}
                    onClick={openFeedbackTips}
                  >
                    <Typography2 component="span" variant="bodyPrimary">
                      {_t('View feedback tips')}
                    </Typography2>
                    <InfoOutlineIcon color="interactive" size="medium" />
                  </TrackedButton>
                )}
                {minCharacterCountEnabled && charCount < 100 && (
                  <div css={styles.peerReviewMinCharCount} aria-live="polite">
                    <Typography2 component="span" variant="bodyPrimary">
                      {_t('Minimum #{minCount} characters: #{charCount}', {
                        minCount,
                        charCount,
                      })}
                    </Typography2>
                  </div>
                )}
              </React.Fragment>
            );
          }}
        </ReduceLowValueFeedbackContext.Consumer>
      </div>
    );
  }
}

export default MultiLineInputFormPart;
